import React, { FC, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { ColumnProps } from 'antd/lib/table';
import {
    Table,
    Row,
    Col,
    Tag,
    Divider,
    Popconfirm,
    Button,
    Input,
    Breadcrumb,
    Icon,
} from 'antd';

import { User } from '../../store/api/apiTypes';
import { list, del, UsersState, getUsersListState,
    getUserDeleteState, getUserCreateState, getUserUpdateState } from '../../store/actions/users';
import { MainReducerState } from '../../store/reducers';

import { constants, isAdmin, getUserRoleColor, getUserRoleName } from '../../utils';
import UserDrawer from './UserDrawer';
import { InputProps, SearchProps } from 'antd/lib/input';
import { getRoute, RoutePathName } from '../../routes';

interface UsersProps {
    creates: UsersState['create'];
    deletes: UsersState['delete'];
    deleteUser: typeof del.trigger;
    loadUsers: typeof list.trigger;
    updates: UsersState['update'];
    usersState: UsersState['list'];
}

const Users: FC<UsersProps> = ({ creates, deletes, deleteUser, loadUsers, updates, usersState }) => {

    const [ isFormVisible, setIsFormVisible ] = useState(false);
    const [ selectedId, setSelectedId ] = useState<string>();
    const [ search, setSearch ] = useState<string>();

    useEffect(() => {
        loadUsers({
            limit: constants.PAGE_SIZE,
        });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const params: any = {
            limit: constants.PAGE_SIZE,
        };
        if (search && search.length > 1) {
            params.searchString = search;
            params.throttling = 300;
        }
        loadUsers(params);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (creates.success || deletes.success || updates.success) {
            loadUsers({
                limit: constants.PAGE_SIZE,
            });
        }
    }, [creates.success, deletes.success, updates.success]); // eslint-disable-line react-hooks/exhaustive-deps

    const setEditMode = (id: string) => {
        setSelectedId(id);
        setIsFormVisible(true);
    };

    const onSearchChange: InputProps['onChange'] = (e) => {
        setSearch(e.target.value);
    };

    const onSearch: SearchProps['onSearch'] = (value) => {
        setSearch(value);
    };

    const onAdd = () => {
        setSelectedId(undefined);
        setIsFormVisible(true);
    };

    const onDeleteElement = (elem: User['id']) => {
        deleteUser({ id: elem });
    };

    const onDrawerClose = () => {
        setIsFormVisible(false);
    };

    const handleTableChange = (pagination: any, filters: any, sorter: any) => {
      loadUsers({
        page: pagination.current - 1,
        pageSize: pagination.pageSize,
        limit: constants.PAGE_SIZE,
      });
    };

    const rowKey = (item: User) => `${item.id}`;
    const columns: Array<ColumnProps<User>> = [
        {
            dataIndex: 'lastName',
            title: 'Nom',
        },
        {
            dataIndex: 'firstName',
            title: 'Prénom',
        },
        {
            dataIndex: 'position',
            title: 'Fonction',
        },
        {
            dataIndex: 'email',
            title: 'Email',
        },
        {
            dataIndex: 'phone',
            title: 'Téléphone',
        },
        {
            dataIndex: 'role',
            title: 'Droits',
            render: (value) => <Tag color={getUserRoleColor(value)}>{getUserRoleName(value)}</Tag>,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text, record) => isAdmin && (
                <>
                    <Button
                        icon="edit"
                        className="list-action-button-edit"
                        onClick={setEditMode.bind(null, record.id)}
                    />
                    <Divider type="vertical"/>
                    <Popconfirm
                        title="Voulez-vous vraiment supprimer?"
                        onConfirm={onDeleteElement.bind(null, record.id)}
                        okText="Oui"
                        cancelText="Non"
                    >
                        <Button icon="delete" type="danger" />
                    </Popconfirm>
                </>
            ),
        },
    ];
    return (
        <>
        {usersState && usersState.data && (
            <>
            <Row align="middle" gutter={[8, 40]}>
                <Col>
                    <Breadcrumb>
                        <Breadcrumb.Item href={getRoute(RoutePathName.documents, {})} >
                            <Icon type="home" />
                        </Breadcrumb.Item>
                        <Breadcrumb.Item href={getRoute(RoutePathName.users, {})}>
                            <Icon type="user" />
                            <span>Utilisateurs</span>
                        </Breadcrumb.Item>
                    </Breadcrumb>
                </Col>
            </Row>
            <Row align="middle" gutter={[8, 40]}>
                <Col xs={8} sm={10} md={12} lg={14} xl={16}>
                    <Input.Search
                        className="table-search"
                        placeholder="Rechercher"
                        onChange={onSearchChange}
                        onSearch={onSearch}
                        size="small"
                    />
                </Col>
                <Col xs={1} sm={4} md={4} lg={4} xl={4} push={4}>
                    <Button
                        type="primary"
                        icon="plus-circle"
                        className="list-create-element"
                        onClick={onAdd}
                    >
                        Ajouter un utilisateur
                    </Button>
                </Col>
            </Row>
            <Row gutter={[8, 40]}>
                <Table<User>
                    rowKey={rowKey}
                    columns={columns}
                    loading={usersState.loading}
                    dataSource={usersState.data.items}
                    pagination={{
                        total: usersState.data.totalCount,
                        current: usersState.data.page + 1,
                        pageSize: usersState.data.pageSize,
                    }}
                    onChange={handleTableChange}
                />
            </Row>
            <UserDrawer
                id={selectedId}
                isVisible={isFormVisible}
                onClose={onDrawerClose}
            />
            </>
        )}
        </>
    );
};

const mapStateToProps = (state: MainReducerState) => ({
    creates: getUserCreateState(state),
    deletes: getUserDeleteState(state),
    updates: getUserUpdateState(state),
    usersState: getUsersListState(state),
});

export default connect(
    mapStateToProps,
    {
        loadUsers: list.trigger,
        deleteUser: del.trigger,
    },
)(Users);
